import { useEffect } from "react";
import { Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, passwordForgotSchema } from "@utils/index";
import { PasswordForgotFormInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { useUserPasswordForgot } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormRow,
  FormInput,
  Button,
  RegistrationButton,
  AuthLinks,
  FormHeaderNotAuth,
  ArrowLeftSvg,
} from "@components/index";

export const PasswordForgotForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailUrlValue = location.search.split("email=")[1];
  const isAuth = useCommonStore(({ isAuth }) => isAuth);
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();

  const { mutate, isPending: isPendingPasswordForgot } = useUserPasswordForgot(
    () => {
      reset();
      notify(
        "success",
        "If you have an account, in the next few seconds you will receive a link to your email address with instructions to complete your password reset.",
      );
      navigate("/login");
    },
    ({ message, fields }) => {
      Object.keys(fields || {}).forEach((key) =>
        setError(key as "email", { message: fields![key] }),
      );
      notify("error", message);
    },
  );

  useEffect(() => {
    setState({ isLoading: isPendingPasswordForgot });
  }, [isPendingPasswordForgot, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
  } = useForm<PasswordForgotFormInterface>({
    mode: "onBlur",
    disabled: isPendingPasswordForgot,
    resolver: yupResolver(passwordForgotSchema),
  });

  useEffect(() => {
    if (emailUrlValue) {
      reset({ email: emailUrlValue });
    }
  }, [reset, emailUrlValue]);

  const onSubmit: SubmitHandler<PasswordForgotFormInterface> = (fields) =>
    mutate({ ...fields });

  if (isAuth) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="md:mx-auto md:max-w-[440px] md:pt-[218px]"
      >
        <FormHeaderNotAuth
          title="Forgot password?"
          subTitle="Enter your details below"
        />
        <FormRow top="medium">
          <FormInput
            {...register<"email">("email")}
            name="email"
            label="Email"
            error={formState.errors.email?.message}
            onChange={({ currentTarget }) => {
              setValue("email", currentTarget.value.trim());
              clearErrors("email");
            }}
          />
        </FormRow>
        <FormRow top="medium">
          <Button type="submit" title="Submit" />
        </FormRow>
        <FormRow top="small">
          <Link
            to="/login"
            className="mx-auto flex w-fit items-center gap-x-2 text-200 font-bold text-black-54 hover:underline"
          >
            <ArrowLeftSvg />
            Back to sign in
          </Link>
        </FormRow>
        {isMobile && (
          <FormRow top="large">
            <AuthLinks />
          </FormRow>
        )}
      </Form>
      {isMobile && <RegistrationButton />}
      {!isMobile && <AuthLinks />}
    </>
  );
};
